import { Component, Suspense, useState, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, Eye, Loader2, RotateCcw } from 'lucide-react';
import type { PluginMarketingPreviewSurface } from '@reflex/plugin-api';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';

interface PluginPreviewFrameProps {
  /** Heading rendered above the preview body. */
  label: string;
  /** Optional caption rendered under the heading. */
  description?: string;
  /** Surface hint that controls the body height/aspect ratio. */
  surface: PluginMarketingPreviewSurface;
  /** Stable testid suffix (typically the preview's `id`). */
  testIdSuffix: string;
  children: ReactNode;
}

/**
 * Maps each preview surface to a `preview-surface-*` utility (defined in
 * `ui/src/styles/index.css`). Sizes the body so full pages get an actual
 * page-shaped frame, dashboard widgets stay compact, etc. The wrapper
 * itself remains scrollable so a long full-page preview doesn't blow out
 * the detail layout.
 */
const SURFACE_CLASS: Record<PluginMarketingPreviewSurface, string> = {
  page: 'preview-surface-page',
  panel: 'preview-surface-panel',
  card: 'preview-surface-card',
  widget: 'preview-surface-widget',
  badge: 'preview-surface-badge',
};

/**
 * Frames a plugin's live marketing preview. Provides a labelled chrome
 * with a surface chip, "Sample data" badge, a refresh button that
 * remounts stateful previews, a loading state via Suspense, and a
 * graceful fallback when the preview throws.
 *
 * The previewed component is responsible for being self-contained — it
 * must seed any caches it needs and avoid hitting real APIs. This frame
 * does not pass props to the child.
 */
export function PluginPreviewFrame({
  label,
  description,
  surface,
  testIdSuffix,
  children,
}: PluginPreviewFrameProps) {
  // Bumping `nonce` swaps the React key on the child subtree so any
  // stateful preview (typing animations, expanded rows, demo
  // interactions) snaps back to its initial render.
  const [nonce, setNonce] = useState(0);

  return (
    <article
      data-testid={`plugin-previews-section-${testIdSuffix}`}
      data-surface={surface}
      className="overflow-hidden rounded-xl border border-border/50 bg-card/40"
    >
      <header className="flex items-start justify-between gap-3 border-b border-border/40 bg-muted/30 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Eye className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <PanelTitle as="h3" size="sm">
              {label}
            </PanelTitle>
          </div>
          {description && <p className="mt-1 text-xs text-muted-foreground/80">{description}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge
            variant="outline"
            data-testid={`plugin-previews-section-${testIdSuffix}-surface`}
            className="text-3xs uppercase tracking-wider"
          >
            {surface}
          </Badge>
          <Badge variant="outline" className="text-3xs uppercase tracking-wider">
            Sample data
          </Badge>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid={`plugin-previews-section-${testIdSuffix}-refresh`}
            aria-label="Refresh preview"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            onClick={() => setNonce((n) => n + 1)}
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
        </div>
      </header>
      <div className={cn('preview-canvas p-2', SURFACE_CLASS[surface])}>
        <div
          data-testid={`plugin-previews-section-${testIdSuffix}-body`}
          className="relative h-full overflow-auto rounded-md border border-border/50 bg-background"
        >
          <PreviewErrorBoundary previewId={testIdSuffix} key={nonce}>
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs">Loading preview…</span>
                </div>
              }
            >
              {children}
            </Suspense>
          </PreviewErrorBoundary>
        </div>
      </div>
    </article>
  );
}

interface PreviewErrorBoundaryProps {
  previewId: string;
  children: ReactNode;
}

interface PreviewErrorBoundaryState {
  error: Error | null;
}

class PreviewErrorBoundary extends Component<PreviewErrorBoundaryProps, PreviewErrorBoundaryState> {
  state: PreviewErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): PreviewErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Marketing previews are best-effort — keep noise out of error trackers
    // but log enough breadcrumbs that an engineer can trace why the preview
    // didn't render.
    console.warn(
      `[plugin-preview] preview "${this.props.previewId}" threw during render`,
      error,
      info,
    );
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex h-full items-center justify-center p-4">
          <Alert
            variant="destructive"
            data-testid={`plugin-previews-section-${this.props.previewId}-error`}
          >
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Preview unavailable</AlertTitle>
            <AlertDescription>The preview component crashed while rendering.</AlertDescription>
          </Alert>
        </div>
      );
    }
    return this.props.children;
  }
}
